import React from "react";
import { ClipboardCopy, ArrowRightCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * SeedIntentRow — Sub-sprint RL-SEED-MEMBERSHIPS-HELPER.
 *
 * Renders one intent row. The "Load into Promote form" button never
 * applies anything: it only navigates to `/admin/racket-lab/ops/promote`
 * with `location.state.prefillIntent` so the operator must still hit
 * Dry-run + typed-confirm "APPLY" in the S6 page.
 */
const STATUS_COLORS = {
  missing_user: "bg-slate-100 text-slate-700 border-slate-300",
  already_active: "bg-emerald-100 text-emerald-800 border-emerald-300",
  role_mismatch: "bg-amber-100 text-amber-900 border-amber-300",
  revoked: "bg-rose-100 text-rose-800 border-rose-300",
  ready: "bg-sky-100 text-sky-900 border-sky-300",
  pending: "bg-violet-100 text-violet-900 border-violet-300",
};

const StatusPill = ({ status, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_COLORS[status] || STATUS_COLORS.pending}`}
  >
    {status}
  </span>
);

const SeedIntentRow = ({ row }) => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { row_index: i, intent, status, current_role, current_status, warnings = [], display_label } = row;
  const testId = `rl-ops-seed-helper-row-${i}`;

  const onLoad = () => {
    navigate("/admin/racket-lab/ops/promote", {
      state: {
        prefillIntent: {
          user_id: intent.user_id,
          role: intent.role,
          organisation_id: intent.organisation_id,
          shop_ids: intent.shop_ids || [],
          seed_run_id: row.seed_run_id,
          source: `seed_helper.row_${i}`,
        },
      },
    });
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(intent, null, 2));
    } catch {
      // Clipboard may be blocked in some browsers; silent no-op is fine.
    }
  };

  return (
    <tr data-testid={testId} className="border-t border-slate-100 align-top text-xs">
      <td className="px-2 py-2 text-slate-500" data-testid={`${testId}-index`}>{i}</td>
      <td className="px-2 py-2">
        <div className="font-mono text-slate-800" data-testid={`${testId}-user-id`}>{intent.user_id || "—"}</div>
        {display_label && <div className="text-[10px] text-slate-500">{display_label}</div>}
        {warnings.includes("synthetic_user_id") && (
          <span
            data-testid={`${testId}-warning-synthetic`}
            className="mt-1 inline-flex rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800"
          >
            {t("ops.seed_helper.row.synthetic_user_id_warning")}
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-slate-700" data-testid={`${testId}-role`}>{intent.role}</td>
      <td className="px-2 py-2 text-slate-700">
        {(intent.shop_ids || []).length === 0 ? (
          <span className="text-slate-400">—</span>
        ) : (
          <ul className="flex flex-wrap gap-1" data-testid={`${testId}-shops`}>
            {intent.shop_ids.map((sid) => (
              <li key={sid} className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700">{sid}</li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-2 py-2"><StatusPill status={status} testId={`${testId}-status`} /></td>
      <td className="px-2 py-2 text-slate-600" data-testid={`${testId}-current`}>
        {current_role ? <span className="font-mono">{current_role}</span> : <span className="text-slate-400">—</span>}
        {current_status && <div className="text-[10px] text-slate-500">{current_status}</div>}
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            data-testid={`${testId}-load-btn`}
            onClick={onLoad}
            className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-sky-700"
          >
            <ArrowRightCircle className="w-3.5 h-3.5" /> {t("ops.seed_helper.row.load_into_promote")}
          </button>
          <button
            type="button"
            data-testid={`${testId}-copy-json`}
            onClick={onCopy}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50"
          >
            <ClipboardCopy className="w-3 h-3" /> {t("ops.seed_helper.row.copy_json")}
          </button>
        </div>
      </td>
    </tr>
  );
};

export default SeedIntentRow;
